import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { InputComponent } from "../../shared/input/input.component";
import { ButtonComponent } from "../../shared/button/button.component";
import { LayoutComponent } from "../../shared/layout/layout.component";
import { TitleComponent } from "../../shared/title/title.component";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { BreadcrumbComponent } from "../../shared/breadcrumb/breadcrumb.component";
import { PasswordRequirementsComponent } from "../../shared/password-requirements/password-requirements.component";
import { PasswordService } from "../../services/password.service";
import { InvitationService } from "../../services/invitation.service";
import { InviteSessionService, forwardedInviteQueryParams } from "../../services/invite-session.service";
import { ActivationNoticeService } from "../../services/activation-notice.service";
import { RedirectService } from "../../services/redirect.service";
import { Version, VersionService } from "../../services/version.service";

/**
 * Password setup after a validated invitation (docs/invite-flow.md). Sends token + password to
 * the IPA Login UI API, which sets the password and activates the account in CDC.
 */
@Component({
  selector: "app-invite-set-password",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputComponent,
    ButtonComponent,
    LayoutComponent,
    TitleComponent,
    BreadcrumbComponent,
    PasswordRequirementsComponent,
    SpinnerComponent,
  ],
  templateUrl: "./invite-set-password.component.html",
  styleUrl: "./invite-set-password.component.css",
})
export class InviteSetPasswordComponent implements OnInit {
  form: FormGroup;
  validations = { minLength: false, uppercase: false, number: false, specialChar: false };
  submitting = false;
  errorKey: string | null = null;
  layoutType: "blue" | "yellow";

  private token: string | null = null;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private passwordService: PasswordService,
    private invitationService: InvitationService,
    private inviteSession: InviteSessionService,
    private activationNotice: ActivationNoticeService,
    private redirectService: RedirectService,
    private versionService: VersionService,
  ) {
    this.form = this.fb.group({
      newPassword: ["", [Validators.required, this.passwordService.validator()]],
      confirmPassword: ["", [Validators.required]],
    });
    this.form.valueChanges.subscribe(() => (this.errorKey = null));
    this.layoutType = this.versionService.current === Version.B2B ? "blue" : "yellow";
  }

  ngOnInit(): void {
    this.token = this.inviteSession.token;
    if (!this.token) {
      // Opened directly, or the 30-minute window passed: there is no invitation to complete.
      console.warn("InviteSetPasswordComponent.ngOnInit - no invitation token in this session.");
      this.go("/invite/expired");
    }
  }

  get passwordMismatch(): boolean {
    const { newPassword, confirmPassword } = this.form.value;
    return !!newPassword && !!confirmPassword && newPassword !== confirmPassword;
  }

  get canSubmit(): boolean {
    return this.form.valid && !this.passwordMismatch && !this.submitting;
  }

  checkPassword(): void {
    this.validations = this.passwordService.checkStrength(this.form.get("newPassword")?.value || "");
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit || !this.token) return;

    this.submitting = true;
    this.errorKey = null;
    const origin = this.versionService.current === Version.B2B ? "b2b" : "b2c";
    const outcome = await this.invitationService.completeInvitation(this.token, this.form.value.newPassword, origin);
    this.submitting = false;

    switch (outcome.kind) {
      case "activated":
        this.inviteSession.clear();
        this.activationNotice.stage();
        // The BFF takes the redirect from the encrypted link; checking it again keeps a
        // misconfigured payload from turning this page into an open redirect.
        if (outcome.redirect && this.redirectService.redirect(outcome.redirect)) return;
        this.go("/");
        return;
      case "expired":
      case "invalidLink":
        this.inviteSession.clear();
        this.go("/invite/expired");
        return;
      case "passwordRejected":
        this.errorKey = "updatePassword.weakPasswordError";
        return;
      default:
        this.errorKey = "invite.unavailable";
    }
  }

  private go(path: string): void {
    this.router.navigate([path], { queryParams: forwardedInviteQueryParams(), replaceUrl: true });
  }
}
